import React, { createContext, useCallback, useContext, useState } from 'react';
import { useAuth } from './AuthContext'; // For currentUser
import { db } from '../firebase'; // For Firestore operations
import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getProjectMemberPath } from '../utils/collectionPaths';
import { addUserProject, getUserProjects } from '../services/userService';

const ProjectModalContext = createContext();

export const useProjectModal = () => useContext(ProjectModalContext);

export const ProjectModalProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newlyCreatedProjectId, setNewlyCreatedProjectId] = useState(null);

  const openCreateProjectModal = useCallback(() => {
    setIsCreateProjectModalOpen(true);
    setNewProjectName('');
    setFormError('');
  }, []);

  const closeCreateProjectModal = useCallback(() => {
    setIsCreateProjectModalOpen(false);
    setNewProjectName('');
    setFormError('');
    setIsSubmitting(false);
  }, []);

  const handleCreateProject = useCallback(async () => {
    const trimmedName = newProjectName.trim();
    if (!trimmedName) {
      setFormError("Project name cannot be empty.");
      return;
    }
    if (!currentUser) {
      setFormError("You must be logged in to create a project.");
      return;
    }
    setIsSubmitting(true);
    setFormError('');

    try {
      // Only check for conflicts among projects where the user is the owner
      const userProjects = await getUserProjects(currentUser.uid);
      const ownedProjects = userProjects.filter(p => p.role === 'owner');
      const conflict = ownedProjects.some(
        p => (p.project.projectName || '').trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (conflict) {
        setFormError('You already have a project with this name.');
        setIsSubmitting(false);
        return;
      }

      // Create the project document with memberIds array
      const newProjectRef = await addDoc(collection(db, 'projects'), {
        projectName: trimmedName,
        description: '',
        url: '',
        creationDate: serverTimestamp(),
        defaultAudienceCount: 4,
        interviewQuestions: '',
        taskStatus: null,
        taskType: null,
        createdBy: currentUser.uid, // Track who created the project
        memberIds: [currentUser.uid] // CRITICAL: Initialize memberIds for query support
      });

      // Add the creator as a project member with 'owner' role
      const memberData = {
        role: 'owner',
        email: currentUser.email,
        joinedAt: serverTimestamp()
      };

      await setDoc(doc(db, getProjectMemberPath(newProjectRef.id, currentUser.uid)), memberData);

      // Add the project to the user's userProjects collection
      await addUserProject(currentUser.uid, newProjectRef.id, trimmedName, 'owner');

      // Create default placements asynchronously (fire-and-forget)
      // Don't await - let it run in background, user navigates to project immediately
      import('@modules/firebase-core').then(({ PlacementService }) => {
        const placementService = new PlacementService();
        placementService.createDefaultPlacements(newProjectRef.id).catch((error) => {
          console.error('Failed to create default placements:', error);
          // Silent failure - user can create them via button if needed
        });
      });

      setNewlyCreatedProjectId(newProjectRef.id); // Set the ID for navigation
      closeCreateProjectModal(); // Close modal on success
    } catch (err) {
      setFormError('Failed to create project. Please try again.');
      setIsSubmitting(false);
    }
  }, [newProjectName, currentUser, closeCreateProjectModal]);

  const clearNewlyCreatedProjectId = useCallback(() => {
    setNewlyCreatedProjectId(null);
  }, []);

  const value = {
    isCreateProjectModalOpen,
    openCreateProjectModal,
    closeCreateProjectModal,
    newProjectName,
    setNewProjectName,
    formError,
    handleCreateProject,
    isSubmitting,
    newlyCreatedProjectId,
    clearNewlyCreatedProjectId
  };

  return (
    <ProjectModalContext.Provider value={value}>
      {children}
    </ProjectModalContext.Provider>
  );
}; 